import { useEffect, useState } from "react";
import axios from "axios";
import { MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ["all", "Resort Stay", "Cruise", "Group Travel", "Travel Essential", "Seasonal Offer"];

const resolveImg = (url) => (!url ? "" : url.startsWith("http") ? url : `${BACKEND_URL}${url}`);

export const Gallery = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("all");

  useEffect(() => {
    axios.get(`${API}/gallery`).then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  if (items.length === 0) return null;

  const filtered = active === "all" ? items : items.filter((i) => i.category === active);
  const cats = CATEGORIES.filter((c) => c === "all" || items.some((i) => i.category === c));

  return (
    <section id="gallery" data-testid="gallery-section" className="py-20 md:py-32 bg-sand">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-10">
          <p className="uppercase text-sm tracking-[0.2em] font-semibold text-terracotta mb-4">
            Real journeys
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-ink">
            Moments From Past Adventures
          </h2>
          <p className="text-base sm:text-lg leading-relaxed text-ink-soft mt-4">
            A look at the resorts, cruises, and group getaways our travellers have enjoyed.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-10">
          {cats.map((c) => (
            <button
              key={c}
              data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setActive(c)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                active === c ? "bg-terracotta text-white" : "bg-white border border-bone text-ink-soft hover:border-terracotta"
              }`}
            >
              {c === "all" ? "All" : c}
            </button>
          ))}
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {filtered.map((item) => (
            <figure
              key={item.id}
              data-testid={`gallery-item-${item.id}`}
              className="group relative mb-5 break-inside-avoid overflow-hidden rounded-2xl"
            >
              <img
                src={resolveImg(item.image_url)}
                alt={item.property_name || item.location || "Travel memory"}
                className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              {(item.property_name || item.location || item.caption) && (
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-5 pt-10 text-white opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                  {item.property_name && <p className="font-serif text-lg font-semibold">{item.property_name}</p>}
                  {item.location && (
                    <p className="text-sm text-white/85 flex items-center gap-1"><MapPin size={13} /> {item.location}</p>
                  )}
                  {item.caption && <p className="text-xs text-white/70 mt-1">{item.caption}</p>}
                  <span className="inline-block mt-2 text-[11px] uppercase tracking-wide bg-terracotta/90 px-2 py-0.5 rounded-full">{item.category}</span>
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};
